/**
 * The allow-list cs-markdown applies to the HTML that `marked` produces from a
 * help text, exercise instruction or other markdown body.
 *
 * Every `<img>` tag is rewritten into something we know how to show (a video
 * player, a bundled image or icon, a Firebase storage image) or replaced with
 * an INVALID IMAGE marker. Every `<a href>` tag is rewritten into a link we
 * allow (an in-app help link, an external link, one of our static pages, a
 * mail link to our own domain) or replaced with an INVALID LINK marker.
 *
 * This module has no Angular dependencies on purpose, so the allow-list can be
 * unit-tested directly (see markdown-rewrite.test.ts). The component keeps
 * everything that needs it: the network lookup of Firebase image URLs, the
 * sanitising, change detection, and the tap handlers the rewritten HTML calls.
 */
import { buildYoutubeEmbedHtml, extractYoutubeId } from './video-embed';

export interface MarkdownRewriteOptions {
  // true on the iOS native app, where the inline YouTube iframe fails and a
  // thumbnail that opens the native player is emitted instead. only asked when
  // a YouTube video is actually found.
  useNativeYoutubePlayer: () => boolean;
  // the origin our static pages (privacy.html, tos.html) are served from. only
  // asked when such a link is actually found.
  sharedUrlOrigin: () => string;
}

export interface MarkdownRewriteResult {
  html: string;
  // firebase storage paths (still URL-encoded, deduplicated) the caller must
  // resolve to download URLs; the <img> tags holding them are left unchanged
  firebaseImageRefs: string[];
  // true when the html calls csPlayNativeYoutube(), so the caller must register it
  usesNativeYoutubeThumbnail: boolean;
  // true when the html calls openHelpLink(), so the caller must register it
  usesHelpLinks: boolean;
}

export function rewriteMarkdownHtml(htmlText: string, options: MarkdownRewriteOptions): MarkdownRewriteResult {
  // Used to store image ref values to be replaced later on
  let firebaseImageRefs: string[] = [];
  let usesNativeYoutubeThumbnail = false;
  let usesHelpLinks = false;

  htmlText = htmlText.replace(/(\<img .*?">)/gm, (imgtag: string) => {
    // pull the src URL out of the tag, then hand it to extractYoutubeId,
    // which accepts every common YouTube URL form (see video-embed.ts for
    // the full list). The old regex only matched the long
    // www.youtube.com/...v= form, so a pasted youtu.be share link fell
    // through to the INVALID IMAGE fallback below (#3725).
    const [, src] = imgtag.match(/src="([^"]+)"/) || [, ''];
    const youtubeId = extractYoutubeId(src);
    if (youtubeId) {
      // on the iOS native app the inline iframe player fails with "Error 153"
      // (capacitor://localhost sends no referrer), so there we emit a
      // thumbnail that opens a full-screen native player instead. every other
      // platform keeps the inline iframe. see video-embed.ts / #3730.
      const useNativePlayer = options.useNativeYoutubePlayer();
      if (useNativePlayer) {
        // tell the caller to register the tap handler the thumbnail's onclick
        // calls. a plain exercise video has no @help: link, so this must not
        // depend on the <a href> pass below finding anything.
        usesNativeYoutubeThumbnail = true;
      }
      return buildYoutubeEmbedHtml(youtubeId, useNativePlayer);
    }

    let [, vimeoId, hash] = imgtag.match(/src="https:\/\/vimeo\.com\/([^%/]+)\/([^%/]+)"/) || [, null, null];
    if (vimeoId) {
      return `<div class="aspect-ratio"><iframe src="https://player.vimeo.com/video/${vimeoId}?autoplay=0&muted=1&title=0&byline=0&portrait=0&h=${hash}" frameborder="0" allowfullscreen></iframe></div>`;
    }

    // a stored image is recognised by its "images%2F" prefix and a file
    // extension — ANY extension, in any case, because the upload side keeps the
    // extension of the picked file verbatim (see buildImageVariantKey) and the
    // prefix is what actually identifies a storage reference. [^"] keeps the
    // capture inside the src attribute, so an alt text cannot leak into the
    // path. the pattern this replaced required a lowercase "g" at the end, so
    // an "IMG_4312.JPG" key fell through to INVALID IMAGE below; and it was
    // greedy, so the captured path ran past the src attribute and the caller
    // sent that malformed path to the server and deduplicated on it. (deferred
    // finding df-bc58ab2c2f50. the commit that changed this line says the
    // greedy capture broke the image too — it did not; the browser truncated
    // the rebuilt src at the stray quote and the picture still rendered.)
    let [, imagePath] = imgtag.match(/src="(images%2F[^"]*\.[A-Za-z0-9]+)"/) || [, null];
    if (imagePath) {
      // Pushes an image path (ref) outside, as replace is not an async function
      firebaseImageRefs.push(imagePath);
      return imgtag;
    }
    // images from within the app can be used directly
    let [, assetImgPath] = imgtag.match(/src="(assets\/imgs\/.+)"/) || [, null];
    if (assetImgPath) {
      return imgtag;
    }
    // icons from within the app can be used directly, but must be smaller
    let [, assetIcon] = imgtag.match(/src="assets\/icons\/(.+).svg"/) || [, null];
    if (assetIcon) {
      // remove the "-outline" suffix if there
      assetIcon = assetIcon.replace(/-outline$/, '');
      return `<span class="icon"><cs-icon class="${assetIcon}"></cs-icon></span>`;
    }

    // don't allow anything we don't understand
    return '<br><i>INVALID IMAGE<i><br>';
  });

  htmlText = htmlText.replace(/(<a href="[^"]+">)/gm, (ahreftag: string) => {
    // adjust eventual @help: links
    let [, helpId] = ahreftag.match(/"(@help:.+)"/) || [, null];
    if (helpId) {
      usesHelpLinks = true;
      return `<a href="" onclick="openHelpLink('${helpId}'); return false;">`;
    }
    // reroute external links to a different window
    let [, externUrl] = ahreftag.match(/"(http.+)"/) || [, null];
    if (externUrl) {
      return `<a href="${externUrl}" target="_blank">`;
    }

    // allow specific static pages
    let [, staticUrl] = ahreftag.match(/"\/(.+\.html)"/) || [, null];
    if (['privacy.html', 'tos.html'].includes(staticUrl)) {
      return `<a href="${options.sharedUrlOrigin()}/${staticUrl}" target="_blank">`;
    }

    // allow mailto links as long as they are to our domain
    let [, mailto] = ahreftag.match(/"mailto:([^\?]+)"/) || [, null, null];
    if (mailto && mailto.endsWith('@mh-physique.app')) {
      return `<a href="mailto:${mailto}">`;
    }

    // don't allow anything we don't understand
    return '<br><i>INVALID LINK<i><br>';
  });

  // Dedupe to avoid multiple replacements in the caller's resolution loop
  firebaseImageRefs = [...new Set(firebaseImageRefs)];

  return { html: htmlText, firebaseImageRefs, usesNativeYoutubeThumbnail, usesHelpLinks };
}
